import { isCurved, isFolded, } from '@signa/shared';
import { composePlan, } from './compose-plan.js';
import { curvedDrawPlan, curvedLayout } from './curve.js';
import { drawCurvedPlan } from './curve-draw.js';
import { cellContent, frameLayout, panelContent } from './fit.js';
import { faceDraws, foldedLayout, foldsToStage } from './folds.js';
import { createProgram, createSourceTexture, deleteProgram } from './gl-context.js';
import { apply, invert, rectToQuad, toColumnMajor, DegenerateQuadError } from './homography.js';
import { quadToStage } from './layout.js';
import { naturalSizeOf, needsRealloc } from './source.js';
/**
 * 合成器を作る。寸法が取れない・WebGL2 が使えないときは `null`。
 *
 * **重い準備をここで済ませる** —— コンテキスト・シェーダ・**背景と障害物マスクの層**。
 * `draw()` は動画枠を描いて重ねるだけになる。
 *
 * **動画の書き出しでは `sizing.even` を指定する**(#356 —— H.264 は偶数の寸法しか受け付けない)。
 * `canvas` の寸法はその指定に従うので、**符号化の対応の確認にはこの `canvas` の寸法を渡す。**
 */
export function createComposer(options, sizing = {}) {
    const plan = composePlan(options.template, sizing);
    if (plan === null)
        return null;
    const canvas = document.createElement('canvas');
    canvas.width = plan.size.width;
    canvas.height = plan.size.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null)
        return null;
    const gl = createGl(plan);
    if (gl === null)
        return null;
    let program;
    try {
        program = createProgram(gl.context);
    }
    catch {
        gl.dispose();
        return null;
    }
    /*
     * **背景と障害物マスクは動かない。** テンプレートも背景画像も書き出しの間は変わらないので、
     * **層を 1 度だけ作って毎フレーム `drawImage` する。**
     *
     * ぼかしマスクは白ポリゴンのラスタライズと合成を伴うため、
     * **毎フレーム描くと 1800 回払うことになる。**
     */
    const masks = options.background === null ? null : buildMaskLayer(plan, options.background);
    /*
     * **背景も 1 度だけ焼く**(#181 の実測)。
     *
     * 背景写真は出力寸法と**一致しない**(例: 1800×1200 の写真を 1920×1080 へ)。
     * `drawImage` に寸法を渡すと**毎フレーム拡大縮小が走り**、利用者の環境で
     * **合成が 62.98ms/枚**になっていた —— 30fps の予算(33ms)を超え、
     * **書き出しが 10fps に落ちていた**(実測: 30 秒の出力に 301 枚しか入らない)。
     *
     * ここで出力寸法に焼いておけば、毎フレームは**等倍のコピー**で済む。
     */
    const backgroundLayer = options.background === null ? null : bakeBackground(plan, options.background);
    const textures = new Map();
    /**
     * **曲面の枠の描画計画**(#361)。合成器はテンプレートと出力の寸法が決まっているので、**枠ごとに 1 度だけ作る** ——
     * 書き出しは数百〜千枚を描く(片が多いと、毎枚作り直すだけで 1 枚の予算を食う)。
     */
    const curvedPlans = new Map();
    return {
        canvas,
        draw() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // ① 背景。**焼いた層を等倍で置く**(拡大縮小は `bakeBackground` で済ませてある)。
            if (backgroundLayer !== null)
                ctx.drawImage(backgroundLayer, 0, 0);
            // ② 動画枠。
            drawFrames(gl.context, program, plan, options.sourceAt, textures, curvedPlans);
            ctx.drawImage(gl.canvas, 0, 0);
            // ③ 障害物マスク。**動画より手前**に背景を重ねて遮蔽を作る(設計書 8.4)。
            if (masks !== null)
                ctx.drawImage(masks, 0, 0);
        },
        dispose() {
            for (const entry of textures.values())
                gl.context.deleteTexture(entry.texture);
            textures.clear();
            deleteProgram(program);
            gl.dispose();
        },
    };
}
/**
 * いまの状態を 1 枚の canvas に描く(#180)。
 *
 * 寸法が取れない・WebGL2 が使えないときは `null`。
 * **呼び出し側は「書き出せない」と伝える**(黙って壊れない —— ADR-0036 決定 8)。
 *
 * **返るのは新しい canvas。** 呼び出し側が `toBlob` するなり `VideoFrame` に包むなりできる。
 * WebGL のリソースは戻る前に解放するが、**2D canvas は別物なので残る。**
 */
export function composeFrame(options) {
    const composer = createComposer(options);
    if (composer === null)
        return null;
    try {
        composer.draw();
        return composer.canvas;
    }
    finally {
        composer.dispose();
    }
}
function createGl(plan) {
    const canvas = document.createElement('canvas');
    canvas.width = plan.size.width;
    canvas.height = plan.size.height;
    /*
     * **書き出し用は `preserveDrawingBuffer: true`。**
     *
     * 表示用([`gl-context.ts`](./gl-context.ts))は毎フレーム描き直すので保持しないが、
     * こちらは**描いたあとに `drawImage` で吸い出す**。既定(`false`)でも同じ実行ターン内なら
     * 読めるはずだが、**読めなければ絵が丸ごと落ちる**ので、確実な側に倒す。
     *
     * **表示用の設定は変えていない。** これは書き出しのために作る別のコンテキストである。
     *
     * **`stencil: true` は表示と同じ**(内向きの曲面の隠れ —— #361 / ADR-0060 の決定 5)。
     */
    const context = canvas.getContext('webgl2', {
        alpha: true,
        premultipliedAlpha: false,
        antialias: true,
        stencil: true,
        preserveDrawingBuffer: true,
        powerPreference: 'default',
    });
    if (context === null)
        return null;
    return {
        canvas,
        context,
        dispose: () => {
            context.getExtension('WEBGL_lose_context')?.loseContext();
        },
    };
}
/**
 * 動画枠をすべて描く。**背景は透明。**
 *
 * 表示と同じシェーダ・同じユニフォームを使う。違うのは 2 点だけ:
 *
 * - **倍率は常に 1**(表示は DPR 上限で決まる —— #174)
 * - **ハイライトは常に 0**(選択の強調は編集画面のもの)
 */
function drawFrames(gl, program, plan, sourceAt, textures, 
/** 曲面の枠の描画計画(枠 ID ごと —— 合成器の寿命のあいだ使い回す)。 */
curvedPlans) {
    gl.viewport(0, 0, plan.size.width, plan.size.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(program.program);
    gl.bindVertexArray(program.vao);
    gl.uniform2f(program.uniforms.resolution, plan.size.width, plan.size.height);
    // **等倍。** 表示の倍率(#174)は入れ物と DPR の話で、書き出しには無い。
    gl.uniform1f(program.uniforms.dpr, 1);
    gl.uniform1i(program.uniforms.texture, 0);
    // 選択の強調は編集画面のもの。書き出しには出さない。
    gl.uniform1f(program.uniforms.highlight, 0);
    for (const frame of plan.frames) {
        // **曲面の枠は短冊ごとに描く**(#361 / ADR-0060)。表示側と同じ分け方(折れ目と両方を持つ壊れたデータでは曲面)。
        if (isCurved(frame)) {
            drawCurvedFrame(gl, program, plan.transform, frame.id, frame.curve, frame.grid, frame.fit, sourceAt, textures, curvedPlans);
            continue;
        }
        // **折れた枠は面ごとに描く**(#339 / ADR-0057)。表示側と同じ分け方。
        if (isFolded(frame)) {
            drawFoldedFrame(gl, program, plan.transform, frame.id, frame.quad, frame.grid, frame.folds, frame.fit, sourceAt, textures);
            continue;
        }
        drawFrame(gl, program, plan.transform, frame.id, frame.quad, frame.grid, frame.fit, sourceAt, textures);
    }
    gl.bindVertexArray(null);
}
/**
 * 枠を 1 つ描く。**分割していればセルごと**(#152)。
 *
 * **ホモグラフィは枠に 1 つだけ作って全セルで使い回す**
 * ([ADR-0029](../../../docs/adr/0029-panel-split-grid.md) の決定 3)——
 * セルごとに作ると境界がわずかにずれ、**継ぎ目に隙間が出る。**
 * 表示側([`webgl-renderer.ts`](./webgl-renderer.ts) の `drawFrame`)と同じ手順である。
 */
function drawFrame(gl, program, transform, frameId, quad, grid, 
/** 収め方(#326 / ADR-0056)。未指定は `contain`。 */
fit, sourceAt, textures) {
    const stageQuad = quadToStage(quad, transform);
    const layout = frameLayout(stageQuad, grid);
    // 潰れたクアッドでは描かない。
    if (layout === null)
        return;
    const { panel, cells } = layout;
    let toStage;
    let inverse;
    try {
        toStage = rectToQuad(panel.width, panel.height, stageQuad);
        // **`invert` も同じ try に入れる。** 写す先が決まっても行列が特異になれば投げる。
        inverse = invert(toStage);
    }
    catch (error) {
        if (error instanceof DegenerateQuadError)
            return;
        throw error;
    }
    // 盤面の座標系は全セルで共通。**枠ごとに 1 度だけ渡す。**
    gl.uniformMatrix3fv(program.uniforms.inverse, false, toColumnMajor(inverse));
    gl.uniform2f(program.uniforms.panelSize, panel.width, panel.height);
    for (const cell of cells) {
        drawCell(gl, program, frameId, cell, toStage, fit, sourceAt, textures);
    }
}
/** セルを 1 つ描く。素材が無い・準備前なら描かない。 */
function drawCell(gl, program, frameId, cell, toStage, fit, sourceAt, textures) {
    const source = sourceAt(frameId, cell.index);
    if (source === null)
        return;
    // 準備が済む前は自然サイズが 0(メタデータ待ちの `<video>` / 読み込み前の `<img>`)。
    const size = naturalSizeOf(source);
    if (size.width === 0 || size.height === 0)
        return;
    // **写し元は盤面の矩形**(#147 / ADR-0028)。収め方は枠の `fit` に従う(#326 / ADR-0056)——
    // `contain` なら余りは黒帯、`cover` ならはみ出しはセルの外で切れる。**表示側と同じ関数。**
    const content = cellContent(cell, size, fit);
    if (content === null)
        return;
    if (!prepareTexture(gl, source, size, textures))
        return;
    gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
    // **セルの 4 隅を枠共通のホモグラフィで写す。** 隣り合うセルは盤面座標を
    // 厳密に共有しているので、写したあとも一致する —— 継ぎ目に隙間が出ない。
    const [a, b, c, d] = cell.corners.map((corner) => apply(toStage, corner));
    if (a === undefined || b === undefined || c === undefined || d === undefined)
        return;
    drawQuad(gl, program, [a, b, c, d]);
}
/**
 * **折れた枠**を描く(#339 / [ADR-0057](../../../docs/adr/0057-folded-frame.md) の決定 2)。
 * **表示側([`webgl-renderer.ts`](./webgl-renderer.ts) の `drawFoldedFrame`)と同じ手順** ——
 * 素材はセル 0 の 1 本を展開図全体に収め、面ごとに `uInverse` だけを替えて描く。転送は枠に 1 回。
 */
function drawFoldedFrame(gl, program, transform, frameId, quad, grid, folds, 
/** 収め方(#326 / ADR-0056)。未指定は `contain`。 */
fit, sourceAt, textures) {
    const source = sourceAt(frameId, 0);
    if (source === null)
        return;
    const size = naturalSizeOf(source);
    if (size.width === 0 || size.height === 0)
        return;
    const layout = foldedLayout(quadToStage(quad, transform), grid, foldsToStage(folds, transform));
    if (layout === null)
        return;
    const faces = faceDraws(layout);
    if (faces.length === 0)
        return;
    const content = panelContent(layout.panel, size, fit);
    if (content === null)
        return;
    if (!prepareTexture(gl, source, size, textures))
        return;
    gl.uniform2f(program.uniforms.panelSize, layout.panel.width, layout.panel.height);
    gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
    for (const face of faces) {
        gl.uniformMatrix3fv(program.uniforms.inverse, false, toColumnMajor(face.inverse));
        drawQuad(gl, program, face.corners);
    }
}
/**
 * **曲面の枠**を描く(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 4・5)。
 * **表示側([`webgl-renderer.ts`](./webgl-renderer.ts) の `drawCurvedFrame`)と同じ計画・同じ GL の手順**
 * ([`drawCurvedPlan`](./curve-draw.ts))—— 素材はセルごと、片ごとに `uInverse` だけを替え、内向きの隠れはステンシル。
 */
function drawCurvedFrame(gl, program, transform, frameId, curve, grid, 
/** 収め方(#326 / ADR-0056)。未指定は `contain`。 */
fit, sourceAt, textures, curvedPlans) {
    let plan = curvedPlans.get(frameId);
    if (plan === undefined) {
        const layout = curvedLayout(curve, grid, transform);
        plan = layout === null ? null : curvedDrawPlan(layout);
        curvedPlans.set(frameId, plan);
    }
    if (plan === null)
        return;
    drawCurvedPlan(gl, program, plan, (cell) => {
        const source = sourceAt(frameId, cell.index);
        if (source === null)
            return false;
        // 準備が済む前は自然サイズが 0(メタデータ待ちの `<video>` / 読み込み前の `<img>`)。
        const size = naturalSizeOf(source);
        if (size.width === 0 || size.height === 0)
            return false;
        const content = cellContent(cell, size, fit);
        if (content === null)
            return false;
        if (!prepareTexture(gl, source, size, textures))
            return false;
        gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
        return true;
    });
}
/** 4 隅(表示座標・左上から時計回り)の四角形を三角形 2 つで描く。**表示側と同じ。** */
function drawQuad(gl, program, [a, b, c, d]) {
    gl.bindBuffer(gl.ARRAY_BUFFER, program.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([...a, ...b, ...c, ...a, ...c, ...d]), gl.DYNAMIC_DRAW);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
}
/**
 * 素材のテクスチャを用意して**今の中身を転送する**。描けないとき(作れない・確保に失敗した)は `false`。
 *
 * 平面の枠(セルごと)・折れた枠(枠に 1 回)・曲面の枠(セルごと —— #361)が共有する。
 */
function prepareTexture(gl, source, size, textures) {
    /*
     * **テクスチャは素材ごとに使い回す**(#168 と同じ考え方)。
     *
     * 連続書き出しでは同じ素材を**何百回も**描く。毎回作って捨てると、
     * 確保と解放だけで時間を食う。**同じファイルを複数のセルで共有している場合も
     * 1 つで済む** —— 呼び出し側が同じ要素を返すため、`Map` の鍵が一致する。
     */
    let entry = textures.get(source);
    if (entry === undefined) {
        try {
            entry = { texture: createSourceTexture(gl), allocated: null };
        }
        catch {
            return false;
        }
        textures.set(source, entry);
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, entry.texture);
    /*
     * **動画は毎フレーム転送する。** 表示側は「新しいフレームが来たときだけ」に
     * 絞っている([`shouldUpload`](./source.ts))が、**書き出しは時刻を指定して
     * シークしながら描く**ので、「来たかどうか」ではなく**常に今の中身**が要る。
     *
     * **1 度目は `texImage2D`、以後は `texSubImage2D`**(#168 と同じ)——
     * 前者は毎回テクスチャの形を作り直すが、後者は既にある格納域に書き込むだけで済む。
     */
    if (needsRealloc(entry.allocated, size)) {
        /*
         * **確保できたかを確かめてから記録する**(#170)。
         *
         * 中身の無い動画(`readyState < 2`)や上限を超えた寸法では `INVALID_VALUE` になり、
         * **テクスチャは確保されない。** それを確保済みと記録すると、以後 `texSubImage2D` に進んで
         * **level 0 が無いので永久に書けない** —— **セルが真っ暗になる。**
         */
        gl.getError(); // 前の誤りを自分のものと取り違えないよう掃除する
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
        if (gl.getError() !== gl.NO_ERROR) {
            /*
             * **確保できなかった。テクスチャごと捨てて、次のフレームで作り直す。**
             *
             * **残して再挑戦しても戻らない**(#181 で踏んだ)—— 一度 level 0 の確保に
             * 失敗したテクスチャは "incomplete" のままで、同じ `texImage2D` を繰り返しても
             * **サンプリングは黒を返し続ける。** その結果、**書き出しが最初から最後まで
             * 真っ黒**になった。
             *
             * 捨てておけば次のフレームで新しいテクスチャから始められる。
             */
            gl.deleteTexture(entry.texture);
            textures.delete(source);
            return false;
        }
        entry.allocated = { width: size.width, height: size.height };
    }
    else {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
    }
    return true;
}
// ---------------------------------------------------------------------------
// 障害物マスク(静的な層)
// ---------------------------------------------------------------------------
/**
 * 背景を出力寸法に焼く(#181)。
 *
 * **毎フレームの拡大縮小をやめるため。** 背景写真は出力寸法と一致しないことがあり、
 * `drawImage` に寸法を渡すと**そのたびに再標本化**が走る ——
 * 実測で**合成の大半を占めていた。**
 *
 * 焼けないとき(2D が取れない)は `null`。**呼び出し側は背景無しで描く**
 * より、元の画像をそのまま使うほうがよいので、その場合だけ従来どおり渡す。
 */
function bakeBackground(plan, background) {
    const layer = document.createElement('canvas');
    layer.width = plan.size.width;
    layer.height = plan.size.height;
    const ctx = layer.getContext('2d');
    if (ctx === null)
        return background;
    ctx.drawImage(background, 0, 0, layer.width, layer.height);
    return layer;
}
/**
 * 障害物マスクだけを描いた**透明な層**を作る。
 *
 * **背景画像を障害物の形に切り抜いて動画より手前に重ねる**(設計書 8.4 / PoC 3.6)——
 * 動画を隠すのではなく「背景が手前にある」ように見せるので、
 * 柱の向こうを動画が通り過ぎる表現になる。
 *
 * **1 度だけ作る。** 背景もテンプレートも書き出しの間は変わらない。
 */
function buildMaskLayer(plan, background) {
    if (plan.masks.length === 0)
        return null;
    const layer = document.createElement('canvas');
    layer.width = plan.size.width;
    layer.height = plan.size.height;
    const ctx = layer.getContext('2d');
    if (ctx === null)
        return null;
    for (const mask of plan.masks) {
        drawMask(ctx, mask, background, plan);
    }
    return layer;
}
/** 障害物マスクを 1 つ描く。 */
function drawMask(ctx, mask, background, plan) {
    const { width, height } = plan.size;
    const points = quadToStage(mask.points, plan.transform);
    if (points.length < 3)
        return;
    if (mask.kind === 'clip') {
        ctx.save();
        ctx.beginPath();
        tracePath(ctx, points);
        ctx.clip();
        ctx.drawImage(background, 0, 0, width, height);
        ctx.restore();
        return;
    }
    /*
     * **ぼかすのは輪郭(アルファ)だけ。写真の中身はぼかさない**
     * ([ADR-0034](../../../docs/adr/0034-obstacle-edge-blur.md))。
     *
     * だから「切り抜いた絵に `filter` を掛ける」のではなく、
     * **白いポリゴンをぼかしたものをアルファにして背景を流し込む** ——
     * 表示側が `mask-image` に白ポリゴン + `feGaussianBlur` の SVG を渡しているのと同じ考え方で、
     * CSS の `blur(Npx)` と `feGaussianBlur` の `stdDeviation=N` は同義なので**係数もそのまま**使える。
     */
    const off = document.createElement('canvas');
    off.width = width;
    off.height = height;
    const octx = off.getContext('2d');
    if (octx === null)
        return;
    octx.filter = `blur(${String(mask.stdDeviation)}px)`;
    octx.fillStyle = '#fff';
    octx.beginPath();
    tracePath(octx, points);
    octx.fill();
    // ここから先はぼかさない —— 流し込む写真がぼけてしまう。
    octx.filter = 'none';
    octx.globalCompositeOperation = 'source-in';
    octx.drawImage(background, 0, 0, width, height);
    ctx.drawImage(off, 0, 0);
}
/** 多角形のパスを引く(閉じるのは呼び出し側の `clip` / `fill` に任せる)。 */
function tracePath(ctx, points) {
    const [first, ...rest] = points;
    if (first === undefined)
        return;
    ctx.moveTo(first[0], first[1]);
    for (const [x, y] of rest)
        ctx.lineTo(x, y);
    ctx.closePath();
}
//# sourceMappingURL=compose.js.map